import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import type { SupabaseClient } from '../../lib/supabase';
import { dealKeys } from './hooks';
import { newestFirst } from '../profiles/profileLinks';

/**
 * The CG orders behind a deal, as rows for the drawer's CG orders section.
 *
 * Kyu, 9 September: "if there's a CG order link, it should have its own
 * section as well." Until now the drawer showed the order numbers as text and
 * said "Find it in CG Orders", which is a directory entry, not a link.
 *
 * Read-only, and it reads exactly the columns the row shows. The status
 * label is looked up from cg_statuses by the caller, because the drawer may
 * already hold that list.
 */
const COLUMNS =
  'id, order_no, order_type, status, client, created_at, status_changed_at, date_delivered';

export interface DealOrderRow {
  id: string;
  order_no: string;
  order_type: 'custom' | 'in_stock';
  status: string;
  client: string;
  created_at: string;
  status_changed_at: string | null;
  date_delivered: string | null;
}

export async function fetchOrdersWhere(
  client: SupabaseClient,
  column: 'deal_id' | 'lead_id' | 'company_id',
  id: string,
): Promise<DealOrderRow[]> {
  const { data, error } = await client.from('cg_orders').select(COLUMNS).eq(column, id);
  if (error) {
    throw new Error(error.message);
  }
  // Newest first, by when the order was raised. The one rule for every list.
  return ((data ?? []) as unknown as DealOrderRow[]).sort(newestFirst((row) => row.created_at));
}

export function useDealOrders(dealId: string) {
  return useOrdersWhere('deal_id', dealId);
}

/** The same rows for a lead's or a company's profile. */
export function useOrdersWhere(column: 'deal_id' | 'lead_id' | 'company_id', id: string) {
  const { client } = useAuth();
  return useQuery({
    queryKey: [...dealKeys.all, 'orders', column, id],
    queryFn: () => fetchOrdersWhere(client, column, id),
    enabled: id !== '',
    retry: false,
  });
}
